<template>
    <div class="events-panel">
        <section v-if="title" aria-labelledby="events_title" class="panel panel--content panel--listing events-title" :class="'panel--'+background">
            <div class="panel__body">
                <h2 id="events_title">{{ title }}</h2>
            </div>
        </section>
        <section v-if="hide_filters !== true" aria-labelledby="filter__categories" class="panel panel--content panel--listing event--panel--filters panel--spaced-bottom" :class="'panel--'+background">
            <div class="panel__body">
                <div class="filter">
                    <div class="filter__categories" id="filter__categories">
                        <div class="filter__select">
                            <label>Categories</label>
                            <select v-model="category" id="sort-by-dropdown" class="custom-select form-control" >
                                <option :value="null" selected>All categories</option>
                                <option v-for="option in filtered_categories" :key="option.id" :value="option.slug">
                                    {{ option.name }}
                                </option>
                            </select>
                        </div>
                        <div class="filter__select">
                            <label>From:</label>
                            <vue-pikaday
                                name="trip-start"
                                v-model="pikaday_start"
                                :options="pikaday_options"
                                id="datepicker"
                                class="custom-select form-control"
                                aria-labelledby="datepicker"
                                aria-label="Use the arrow keys to pick a date"
                            />
                        </div>
                        <div class="filter__select">
                            <label>To:</label>
                            <vue-pikaday
                                name="trip-start"
                                v-model="pikaday_end"
                                :options="pikaday_options"
                                id="datepicker"
                                class="custom-select form-control"
                                aria-labelledby="datepicker"
                                aria-label="Use the arrow keys to pick a date"
                            />
                        </div>
                    </div>
                    <div class="filter__input">
                        <div class="filter__box">
                            <input aria-labelledby="event-keyword" id="event-keyword" class="form-control" type="text" placeholder="Filter events by keyword" name="Search field" autocomplete="off" v-model="search" v-debounce:500ms="search_events">
                        </div>
                    </div>
                </div>
            </div>
        </section>
        <section aria-label="events-output" class="panel panel--content panel--spaced-bottom" :class="'panel--'+background">
            <div class="panel__body">
                <h3 v-if="display_date_header" id="datepicker_date_phrase">Showing events from {{ startDate }} to {{ endDate }}</h3>
                <div class="content content--card" style="display: block;">
                    <div v-if="loading_events == true" id="loader-ring">
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                    </div>
                    <ul class="content content--card" v-if="loading_events == false">
                        <template v-for="event in filtered_events">
                            <card 
                                :key="event.id" 
                                :event="event" 
                                :ext_url="use_external_url"
                                :fallback_thumbnail="fallback_thumbnail"
                            ></card>
                        </template>
                    </ul>
                    <div v-if="disable_show_more == false && loading_events == false && filtered_event_by_date.length > filtered_events.length && filtered_events.length > 0">
                        <div class="show-more" style="display: block;">
                            <button @click="show_more_events" type="button" class="button show-more__button loadmore-btn">
                                Show more {{ button_label }}
                            </button>
                        </div>
                    </div>
                    <p v-if="loading_events == false && filtered_events.length == 0">
                        No events to show
                    </p>
                </div>
            </div>
        </section>
	</div>
</template>

<script>
import Card from './card.vue'
import '@netcz/vue-pikaday'
import 'whatwg-fetch'
import mixins from '../mixins.js'

export default {
    components: {
        'card': Card
    },
    props: {
        background: String,
        title: String,
        display_date_header: Boolean,
        hide_filters: Boolean,
        use_external_url: Boolean,
        date_start: Date,
        date_end: Date,
        calendar: Array,
        item_count: Number,
        disable_show_more: Boolean,
        button_label: String,
        fallback_thumbnail: String
    },
    mixins: [ mixins ],
    data() {
        return {
            page_loaded: false,
            // Selected category
            category: null,
            // Dropdown dates
            start_date: null,
            start_date_formatted: null,
            end_date: null,
            end_date_formatted: null,
            // For search field
            search: '',
            // API call data
            events_data: [],
            error_message: null,
            // Loading state
            loading_events: false,
            // Filter down posts for display
            events_total: 0,
            events_on_display: 0,           
            // Temporary containers for data
            filtered_event_by_category: [],
            filtered_event_by_date: [],
            filtered_events: [], // This is the events we are presently displaying
            // Categories arrary for dropdown
            filtered_categories: [],
            // Pikaday options (https://netcz.github.io/vue-pikaday/guide/)
            pikaday_start: null,
            pikaday_end: null,
            pikaday_options: {
                format: 'DD/MM/YYYY'
            }
        }
    },
    computed: {
        startDate() {
            let date = this.start_date.replace(/-/g,"/");
            return date;
        },
        endDate() {
            let date = this.end_date.replace(/-/g,"/");
            return date;
        }
    },
    watch: {
        category() {
            this.filter_by_category();
        },
        pikaday_start() {
            // This is called when the start date datepicker is changed
            this.start_date = this.convert_date_to_ddmmyyyy(this.pikaday_start);
            this.start_date_formatted = this.convert_date_to_pretty_date(this.pikaday_start);
        },
        pikaday_end() {
            // This is called when the end date datepicker is changed
            this.end_date = this.convert_date_to_ddmmyyyy(this.pikaday_end);
            this.end_date_formatted = this.convert_date_to_pretty_date(this.pikaday_end);
        },
        start_date() {
            // This is called on page load
            if(this.page_loaded == true) {
                this.fetch_events_data();
            }
        },
        end_date() {
            if(this.page_loaded == true) {
                this.fetch_events_data();
            }
        }
    },
    methods: {
        convert_date_to_gmt(date) {
            // This is required for the datepicker, converts DD-MM-YYYY to UTC
            let converted_date = date.substr(6, 4)+"-"+date.substr(3, 2)+"-"+date.substr(0, 2)+"T00:00:00";
            return new Date(Date.parse(converted_date)).toUTCString();
        },
        convert_date_to_ddmmyyyy(date) {
            // This is required for API calls, converts the UTC date to DD-MM-YYYY
            let parsed_date = new Date(date);
            var dd = parsed_date.getDate(); 
            var mm = parsed_date.getMonth()+1;
            var yyyy = parsed_date.getFullYear(); 
            if(dd<10) { dd = '0' + dd }
            if(mm<10) { mm = '0' + mm }
            return dd + '-' + mm + '-' + yyyy
        },
        convert_date_to_pretty_date(date) {
            // This is required for API calls, converts the UTC date to DD-MM-YYYY
            let parsed_date = new Date(date);
            var dd = parsed_date.getDate(); 
            var mm = parsed_date.getMonth()+1;
            var yyyy = parsed_date.getFullYear(); 
            if(dd<10) { dd = '0' + dd } 
            if(mm<10) { mm = '0' + mm }
            return dd + '/' + mm + '/' + yyyy
        },
        extract_categories() {
            // This function returns all known categories within the returned posts
            // It will ignore any duplicates, and sorts them alphabetically
            // This is used by the dropdown

            // Loop through each event
            for (var i = 0; i < this.events_data.length; i++) {
                // Loop through the categories in that event
                for (var j = 0; j < this.events_data[i].categories.length; j++) {
                    // Rebuild our object to remove the id
                    let obj = {
                        name: this.events_data[i].categories[j].name,
                        slug: this.events_data[i].categories[j].slug
                    }
                    // Now determine if that category is already in the categories array, if not then add!
                    if(this.filtered_categories.some(e => e.slug === obj.slug)) {
                        // Object exists already in array
                    } else {
                        // Not present so lets add it
                        this.filtered_categories.push(obj);
                    }
                }
            }

            // And quickly sort the filtered categories to be A-Z
            this.filtered_categories.sort(compare);

            function compare(a, b) {
                // Use toUpperCase() to ignore character casing
                const categoryA = a.slug.toUpperCase();
                const categoryB = b.slug.toUpperCase();

                let comparison = 0;
                if (categoryA > categoryB) {
                    comparison = 1;
                } else if (categoryA < categoryB) {
                    comparison = -1;
                }
                return comparison;
            }
        },
        fetch_events_data() {
            // Fetch the data from the Kent API
            
            this.loading_events = true;
                // Create our URLs we use to call the API
                let url = [];
                const promises = [];
                for(let i = 0;i < this.calendar.length;i++){
                    url[i] = 'https://api.kent.ac.uk/api/v1/calendar/' + this.calendar[i]['calendar'] + '/event';
                    let temp_tags = this.calendar[i]['tags'];
                    if(temp_tags.length > 0) {
                        let concatenated_tags = temp_tags.replace(/\s+/g, '-').toLowerCase();
                        url[i] = url[i] + '/tags/' + concatenated_tags;
                    }
                    let temp_categories = this.calendar[i]['categories'];
                    if(temp_categories.length > 0) {
                        let concatenated_category = temp_categories.replace(/\s+/g, '-').toLowerCase();
                        url[i] = url[i] + '/categories/' + concatenated_category;
                    }
                    if(this.start_date) {
                        url[i] = url[i] + '/start/' + this.start_date.replace(/\//g,'-');
                    }
                    if(this.end_date) {
                        url[i] = url[i] + '/end/' + this.end_date.replace(/\//g,'-');
                    }
                    // Prepare the promises
                    promises.push(fetch(url[i]).then(res => res.ok && res.json() || Promise.reject(res)));
                }
                // Fetch from multiple urls; it will iterate through the fetch() in promises
                // It will basically behave the same as before, just adding more events to the data array
                // Documentation here https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Promise/all
                Promise.all(promises).then(data => {
                    // handle data array here
                    for(let i = 0; i < data.length; i++){
                        this.events_data = this.events_data.concat(data[i]);
                    }
                    if(this.page_loaded == false) {
                        this.page_loaded = true;
                    }
                    // Now process the data
                    this.extract_categories();
                    this.filter_by_category();
                })                      
        },
        filter_by_category() {
            // Filter the events by category

            // Set our base number of events to show, this allows the posts count to be reset every refresh
            // If we have specified to show all events then we update the value here to be that of the total event count
            if(this.item_count == 0) {
                this.events_on_display = this.events_data.length;
            } else {
                this.events_on_display = parseInt(this.item_count);               
            }

            // Filter events down to selected category
            let events_by_category = []
            if(this.category == null) {
                events_by_category = this.events_data;
            } else {
                events_by_category = []
                for (var i = 0; i < this.events_data.length; i++) {
                    if(this.events_data[i].categories.some(e => e.slug === this.category)) {
                        events_by_category.push(this.events_data[i]);
                    }
                }
            }
            // Update our array
            this.filtered_event_by_category = events_by_category;
            // Now filter by date
            this.filter_by_date();
        },
        filter_by_date() {
            // Filter the events by date

            let events_by_date = []
            for (var i = 0; i < this.filtered_event_by_category.length; i++) {

                // Convert into proper dates
                let event_start = new Date(this.filtered_event_by_category[i].start.replace(" ", "T"));
                let event_end = new Date(this.filtered_event_by_category[i].end.replace(" ", "T"));
                // Revers the date order, parse it, and convert it into a proper date
                let start_date = this.start_date.substr(6, 4)+"-"+this.start_date.substr(3, 2)+"-"+this.start_date.substr(0, 2)+"T00:00:00";
                let end_date = this.end_date.substr(6, 4)+"-"+this.end_date.substr(3, 2)+"-"+this.end_date.substr(0, 2)+"T00:00:00";
                let start_date_range = new Date(Date.parse(start_date));
                let end_date_range = new Date(Date.parse(end_date));

                // Loop through the filtered events and find those within the date range of the start and end dates
                if(event_start >= start_date_range) {
                    if(event_end <= end_date_range) {
                        events_by_date.push(this.filtered_event_by_category[i]);
                    }
                }

            }
            // Update our array
            this.filtered_event_by_date = events_by_date;
            // Now filter by date
            this.filter_by_number();
        },
        filter_by_number() {
            // Load as many events as specified in our variable
            this.loading_events = false;
            this.filtered_events = this.filtered_event_by_date.slice(0, this.events_on_display);
        },
        search_events() {
            this.filtering = true;

            var results = [];
            this.search = this.trim_string(this.search);
            let search_term = this.search.toUpperCase();

            if(this.search.length > 0){

                for(var i = 0; i < this.filtered_events.length; i++) {
                    for(var key in this.filtered_events[i]) {
                        let value = this.filtered_events[i][key];
                        // Need to loop through the keys rather than specifying them
                        if(typeof value === 'string') {
                            if(value.toUpperCase().indexOf(search_term) !== -1) {
                                if(!this.item_exists(results, this.filtered_events[i])) {
                                    results.push(this.filtered_events[i])
                                }
                            } 
                        }
                    }
                }
                this.filtered_events = results;
            } else {
                this.filter_by_number();
            }

            this.filtering = false;
        },
        show_more_events() {
            this.events_on_display = this.events_on_display + parseInt(this.item_count);
            this.filter_by_number();
        }
    },
    created() {
        // Set the initial display post count value
        this.events_on_display = this.item_count;
        
        // Load the values into the datepickers
        this.pikaday_start = this.convert_date_to_gmt(this.date_start);
        this.pikaday_end = this.convert_date_to_gmt(this.date_end);

        // We set these values for use by the API fetch, etc
        this.start_date = this.date_start;
        this.end_date = this.date_end;

        // Now fetch our events
        this.fetch_events_data();
    },
    beforeDestroy() {
        localStorage.removeItem('eventsArray');
    }
}
</script>
